/**
 * Roles Guide Component
 *
 * A collapsible guide showing all available roles with their descriptions,
 * badges, and permissions.
 */

import { useState } from 'react';
import {
  AdminPanelSettings,
  AttachMoney,
  ExpandLess,
  ExpandMore,
  Info,
  LocalHospital,
  SupervisorAccount,
  Support,
  Vaccines,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  Typography,
} from '@mui/material';
import { getRoleBadgeColor, ROLES } from '@utils/roleUtils';

interface RoleDefinition {
  role: string;
  displayName: string;
  description: string;
  icon: React.ReactNode;
  permissions: string[];
  isBillable?: boolean;
}

const ROLE_DEFINITIONS: RoleDefinition[] = [
  {
    role: ROLES.CLINICIAN,
    displayName: 'Clinician',
    description: 'Healthcare provider who sees patients and creates clinical notes',
    icon: <LocalHospital />,
    permissions: [
      'Create and edit clinical notes',
      'Access patient records',
      'Use AI assistance for documentation',
      'Submit claims for billing (coming soon)',
    ],
    isBillable: true,
  },
  {
    role: ROLES.CLINICAL_STAFF,
    displayName: 'Clinical Staff',
    description:
      'Clinical staff (nurses, medical assistants, techs) who record and document in encounters but do not generate notes',
    icon: <Vaccines />,
    permissions: [
      'Record encounters and dictations',
      'Enter clinical information',
      'Cannot generate or sign notes',
      'Cannot hold the Clinician role (separate seat)',
    ],
    isBillable: true,
  },
  {
    role: ROLES.PRACTICE_ADMINISTRATOR,
    displayName: 'Practice Administrator',
    description: 'Manages organization settings, billing, and user access',
    icon: <AdminPanelSettings />,
    permissions: [
      'Clinician role required to create or edit clinical notes (including recording)',
      'Manage organization members',
      'Access billing portal',
      'Invite and remove users',
      'Manage subscription',
    ],
  },
  {
    role: ROLES.SUPERVISOR,
    displayName: 'Supervisor',
    description: 'Clinical supervisor who oversees other providers',
    icon: <SupervisorAccount />,
    permissions: [
      'Clinician role required to create or edit clinical notes (including recording)',
      'Review team member notes',
      'Approve and sign supervised notes (coming soon)',
    ],
  },
  {
    role: ROLES.OPERATIONS_SUPPORT,
    displayName: 'Operations Support',
    description: 'Administrative staff who support clinical operations',
    icon: <Support />,
    permissions: [
      'View clinical notes (read-only)',
      'Manage patient scheduling (coming soon)',
      'Handle administrative tasks',
      'Generate reports (coming soon)',
    ],
  },
  {
    role: ROLES.BILLING_ADMINISTRATOR,
    displayName: 'Billing Administrator',
    description: 'Manages billing and financial aspects of the practice',
    icon: <AttachMoney />,
    permissions: [
      'Access billing portal',
      'Manage subscription and payments',
      'View financial reports (coming soon)',
      'Handle claims and reimbursements (coming soon)',
    ],
  },
];

interface RolesGuideProps {
  defaultExpanded?: boolean;
  variant?: 'card' | 'inline';
}

export function RolesGuide({ defaultExpanded = false, variant = 'card' }: RolesGuideProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const content = (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Understanding the different roles and their permissions helps you assign the right access
        levels to your team members.
      </Typography>

      {/* Current Roles */}
      <Typography variant="subtitle2" sx={{fontWeight: "bold",  mb: 1 }}>
        Current Roles
      </Typography>
      <List dense>
        {ROLE_DEFINITIONS.map(role => (
          <ListItem
            key={role.role}
            sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>{role.icon}</ListItemIcon>
              <Chip
                label={role.displayName}
                color={getRoleBadgeColor(role.role)}
                size="small"
                sx={{ minWidth: 150 }}
              />
              {role.isBillable && (
                <Chip
                  label="Billable"
                  size="small"
                  variant="outlined"
                  color="success"
                  sx={{ height: 20 }}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ ml: 5, mb: 0.5 }}>
              {role.description}
            </Typography>
            <Box sx={{ ml: 5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: "medium" }}>
                Permissions:
              </Typography>
              <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                {role.permissions.map((permission, idx) => (
                  <li key={idx}>
                    <Typography variant="caption" color="text.secondary">
                      {permission}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1,
          bgcolor: 'info.lighter',
          borderRadius: 1,
        }}
      >
        <Info color="info" sx={{ fontSize: 20, mt: 0.5 }} />
        <Box>
          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
            Important Notes:
          </Typography>
          <Typography variant="caption" sx={{display: "block",  mt: 0.5 }}>
            • <strong>Billable roles</strong> count toward your subscription seats
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            • Users can have multiple roles for flexible permissions
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            • Practice Administrator role includes billing access
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  if (variant === 'inline') {
    return content;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Info />
            <Typography variant="h6">Roles Guide</Typography>
          </Box>
        }
        action={
          <IconButton onClick={() => setExpanded(!expanded)} size="small">
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
      />
      <Collapse in={expanded}>
        <CardContent>{content}</CardContent>
      </Collapse>
    </Card>
  );
}
